import { useBooleanState } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { IconButton } from '@calendly/ui/components/icon-button';
import { ArrowRoundRightIcon, CloseCircleIcon, EditPencilIcon, AtmCardIcon, MailSendEnvelopeIcon, MobilePhoneIcon, TimezoneIcon } from '@calendly/ui/components/icons';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { primaryColorL1 } from '@calendly/ui/theme';
import classNames from 'classnames';
import moment from 'moment';
import { forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { HostReschedule } from 'features/host_reschedule';
import { BOOKING_METHODS } from 'features/one_off_modal';
import { dateNotationSelector } from 'selectors/current_user';
import { userDateFormat } from 'services/time_format';
import { CancelInviteeModal } from './cancel_invitee_modal';
import { getPaymentInfo } from './payment_utils';
import { InfoContainer as Container, Row } from './styles';
import { FullStoryMask } from '../../../../../../shared/fullstory_privacy';
const actionsCss = "a1ce7dn6";
const Actions = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(actionsCss, props.className)} />;
});
const RescheduledBanner = ({
  cancellation,
  newSchedulable
}) => <ContextualBanner variant="warning">
    <BodyText size="medium">
      <strong>
        {I18n.t('publisher.meetings.invitees.rescheduled_by', {
        actor: cancellation.cancelledBy
      })}
        :
      </strong>
      {' '}
      {cancellation.reason}
    </BodyText>
    <BodyText size="medium">
      <strong>{I18n.t('publisher.dashboard.invitee.new_time')}</strong>
      {' '}
      {newSchedulable.eventTime}
    </BodyText>
  </ContextualBanner>;
const CancelledBanner = ({
  cancellation
}) => <ContextualBanner variant="danger">
    <BodyText size="medium">
      <strong>
        {cancellation.attribution}
        :
      </strong>
      {' '}
      {cancellation.reason}
    </BodyText>
  </ContextualBanner>;
const RescheduleButton = ({
  onClick
}) => <MinimalButton onClick={onClick}>
    <ArrowRoundRightIcon spacingRight={4} />
    {I18n.t('publisher.meetings.invitees.reschedule_invitee')}
  </MinimalButton>;
export const InviteeDetails = ({
  event,
  invitee,
  readonly,
  openEditEmailModal
}) => {
  const dateNotation = useSelector(dateNotationSelector);
  const [isCancelInviteeModalOpen, openCancelInviteeModal, closeCancelInviteeModal] = useBooleanState(false);
  const isPoll = event.eventType.bookingMethod === BOOKING_METHODS.poll;
  const {
    uuid,
    name,
    email,
    phoneNumber,
    standardTimezoneTitle,
    timezone,
    createdAt,
    cancelled,
    cancellation,
    newSchedulable,
    charge,
    policy,
    confirmedAt
  } = invitee;
  const createdBy = invitee.inviteeScheduledBy?.name;
  const canReschedule = !readonly && policy?.reschedule && !isPoll;
  const canCancel = !readonly && policy?.cancel;
  const canEditEmail = !readonly && policy?.update;
  const isCancelled = cancelled && !newSchedulable;
  const isRescheduled = cancelled && newSchedulable;
  const hasCharge = charge && charge.captured;
  const formattedCreatedAt = createdAt && userDateFormat(moment(createdAt), dateNotation);
  const formattedConfirmedAt = confirmedAt && userDateFormat(moment(confirmedAt), dateNotation);
  const paymentInfo = getPaymentInfo(charge, hasCharge);
  return <>
      <Container>
        {isCancelled && <CancelledBanner cancellation={cancellation} />}
        {isRescheduled && <RescheduledBanner cancellation={cancellation} newSchedulable={newSchedulable} />}
        <Row>
          <MailSendEnvelopeIcon spacingRight={8} />
          <FullStoryMask>{email}</FullStoryMask>
          {canEditEmail && <IconButton size="small" title={I18n.t('publisher.action.edit')} aria-label={I18n.t('publisher.action.edit')} onClick={openEditEmailModal}>
              <HeadingText color={primaryColorL1}>
                <EditPencilIcon />
              </HeadingText>
            </IconButton>}
        </Row>
        <Row>
          <TimezoneIcon spacingRight={8} />
          <span>{standardTimezoneTitle || timezone}</span>
        </Row>
        {phoneNumber && <Row>
            <MobilePhoneIcon spacingRight={8} />
            <FullStoryMask>{phoneNumber}</FullStoryMask>
          </Row>}
        {hasCharge && <Row>
            <AtmCardIcon spacingRight={8} />
            <FullStoryMask>{paymentInfo}</FullStoryMask>
          </Row>}
        {(canReschedule || canCancel) && <Actions>
            {canReschedule && <HostReschedule event={event} invitee={invitee} isInviteeOnlyReschedule Button={RescheduleButton}>
                <MinimalButton as="a" href={`/reschedulings/${uuid}?&return_to=${window.location.pathname}`}>
                  <ArrowRoundRightIcon spacingRight={4} />
                  {I18n.t('publisher.meetings.invitees.reschedule_invitee')}
                </MinimalButton>
              </HostReschedule>}
            {canCancel && <MinimalButton decoration="danger" onClick={openCancelInviteeModal}>
                <CloseCircleIcon spacingRight={4} />
                {I18n.t('publisher.meetings.invitees.remove_invitee')}
              </MinimalButton>}
          </Actions>}
        {createdAt && <BodyText size="medium" color="muted">
            {createdBy ? I18n.t('publisher.meetings.invitees.scheduled_at_by', {
          date: formattedCreatedAt,
          actor: createdBy
        }) : I18n.t('publisher.meetings.invitees.scheduled_at', {
          date: formattedCreatedAt
        })}
            {confirmedAt && ` ${I18n.t('publisher.meetings.invitees.confirmed_at', {
          date: formattedConfirmedAt,
          actor: name
        })}`}
          </BodyText>}
      </Container>
      {isCancelInviteeModalOpen && <CancelInviteeModal invitee={invitee} onClose={closeCancelInviteeModal} />}
    </>;
};

require("./invitee_details.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./invitee_details.js");